import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Globe, Code } from 'lucide-react';

const About = () => {
    return (
        <div className="min-h-screen bg-cyber-gray text-white font-rajdhani relative overflow-hidden">
            {/* Background Grid Animation */}
            <div className="absolute inset-0 z-0 opacity-10 pointer-events-none">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-20"
                >
                    <span className="text-neon-blue font-mono tracking-widest text-sm uppercase mb-4 block">Manifesto</span>
                    <h1 className="text-5xl md:text-7xl font-bold font-orbitron bg-clip-text text-transparent bg-gradient-to-r from-neon-blue via-white to-neon-purple mb-6">
                        MISSION OBJECTIVE
                    </h1>
                    <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                        To architect the substrates of the future, enabling humanity to transcend seamless computation barriers through automated intelligence.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-32">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className="space-y-6"
                    >
                        <h2 className="text-3xl font-bold font-orbitron text-white">The Genesis Protocol</h2>
                        <p className="text-gray-400 text-lg leading-relaxed">
                            VectorShift was initialized with a singular primitive: adapt or obsolesce. We saw a digital landscape fractured by inefficiency and latency. Our directive was clear—unify, optimize, and accelerate.
                        </p>
                        <p className="text-gray-400 text-lg leading-relaxed">
                            We are not just building tools; we are forging the nervous system of the next internet, where data flows without friction and logic executes with crystalline precision.
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-neon-blue to-neon-purple rounded-full blur-3xl opacity-20 animate-pulse"></div>
                        <div className="relative bg-gray-900 border border-gray-700 p-8 rounded-2xl grid grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-800 rounded-lg text-center">
                                <Users className="w-8 h-8 text-neon-blue mx-auto mb-2" />
                                <span className="block text-2xl font-bold font-orbitron">10k+</span>
                                <span className="text-xs text-gray-500 uppercase">Operatives</span>
                            </div>
                            <div className="p-4 bg-gray-800 rounded-lg text-center">
                                <Target className="w-8 h-8 text-neon-purple mx-auto mb-2" />
                                <span className="block text-2xl font-bold font-orbitron">99.9%</span>
                                <span className="text-xs text-gray-500 uppercase">Uptime</span>
                            </div>
                            <div className="p-4 bg-gray-800 rounded-lg text-center">
                                <Globe className="w-8 h-8 text-green-400 mx-auto mb-2" />
                                <span className="block text-2xl font-bold font-orbitron">50+</span>
                                <span className="text-xs text-gray-500 uppercase">Regions</span>
                            </div>
                            <div className="p-4 bg-gray-800 rounded-lg text-center">
                                <Code className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                                <span className="block text-2xl font-bold font-orbitron">1M+</span>
                                <span className="text-xs text-gray-500 uppercase">Executions</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default About;
